import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Zap, FolderOpen, FileText, AlertTriangle, RefreshCw, Send, Check, X, Loader2, FileUp, StickyNote, Pin, FileDown } from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import RecordDocuments from '@/components/RecordDocuments';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = v => { if (!v) return '$0'; if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };
const STATUS_COLOR = { active: C.green, settled: C.green, accepted: C.green, bound: C.green, declined: C.red, quote_generated: C.teal, offered: C.blue, held_for_review: C.amber, filed: C.slate };
const TYPE_STYLE = {
  policy: { color: C.blue, icon: FileText },
  claim: { color: C.red, icon: AlertTriangle },
  renewal: { color: C.teal, icon: RefreshCw },
  submission: { color: C.amber, icon: Send },
  document: { color: C.slate, icon: FileUp },
};

export default function BrokerClientFilePage() {
  const { entityId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('timeline');

  const load = useCallback(() => {
    api.get(`/broker/clients/${entityId}`)
      .then(r => setFile(r.data))
      .catch(err => setError(err?.response?.data?.detail || 'Failed to load client file'));
  }, [api, entityId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-6 text-sm" style={{ color: C.red }} data-testid="client-file-error">{error}</div>;
  if (!file) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  const e = file.entity;
  const s = file.summary;
  const tabs = [
    ['timeline', `Timeline (${(file.timeline || []).length})`],
    ['policies', `Policies (${file.policies.length})`],
    ['claims', `Claims (${file.claims.length})`],
    ['renewals', `Renewals (${file.renewals.length})`],
    ['documents', 'Documents'],
  ];

  return (
    <div className="space-y-4" data-testid="broker-client-file">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/clients')} className="p-2 rounded-md hover:bg-gray-100 mt-0.5" data-testid="client-file-back">
          <ArrowLeft className="w-4 h-4" style={{ color: C.blue }} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold" style={{ color: C.navy }} data-testid="client-file-name">{e.legal_name}</h2>
            {file.is_prospect && <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }}>PROSPECT</span>}
          </div>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            <RegistryLink abn={e.abn} nzbn={e.nzbn} style={{ color: C.slate }} />
            {e.industry_code ? ` · ${e.industry_code}` : ''}{e.state ? ` · ${e.suburb || ''} ${e.state}` : ''}
          </p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/loss-run/${e.entity_id}`}
          target="_blank" rel="noopener noreferrer" data-testid="client-loss-run-btn"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold shrink-0 transition-colors border hover:bg-[#F5F7FA]"
          style={{ color: '#1A4C7C', borderColor: 'rgba(26,76,124,0.3)' }}
          title="Download the loss run report (claims history & incurred totals)">
          <FileDown className="w-3.5 h-3.5" />Loss Run
        </a>
        <button onClick={() => navigate(`/new-business?tab=submit&entity_id=${e.entity_id}`)}
          data-testid="client-quick-quote-btn"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white shrink-0 transition-opacity hover:opacity-90"
          style={{ background: '#FF9933' }}>
          <Zap className="w-3.5 h-3.5" />Quick Quote
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI label="Active Policies" value={s.active_policies} color={C.blue} />
        <KPI label="GWP" value={fmt$(s.gwp)} color={C.navy} />
        <KPI label="Pending Quotes" value={s.pending_quotes} color={s.pending_quotes > 0 ? C.amber : C.slate} />
        <KPI label="Open Claims" value={s.open_claims} color={s.open_claims > 0 ? C.red : C.green} />
        <KPI label="Claims Paid" value={fmt$(s.claims_paid)} color={C.slate} />
        <KPI label="Renewals" value={s.renewals} color={C.teal} />
      </div>

      <BrokerNotes clientKey={entityId} />

      <div className="flex gap-1 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`client-tab-${id}`}
            className="px-4 py-2 text-xs font-medium transition-colors"
            style={{ color: tab === id ? C.blue : C.slate, borderBottom: tab === id ? `2px solid ${C.blue}` : '2px solid transparent' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'timeline' && <TimelineCard items={file.timeline || []} />}
      {tab === 'policies' && <ListCard rows={file.policies} empty="No policies yet — use Quick Quote to write the first one."
        render={p => [p.policy_number, p.class, fmt$(p.annual_premium), p.expiry_date ? `exp ${new Date(p.expiry_date).toLocaleDateString('en-AU')}` : '', p.status]} testid="client-policies" />}
      {tab === 'claims' && <ListCard rows={file.claims} empty="No claims on this client."
        render={c => [c.claim_reference, c.claim_type, fmt$(c.estimated_quantum), `paid ${fmt$(c.paid_amount)}`, c.status]} testid="client-claims" />}
      {tab === 'renewals' && <RenewalsCard rows={file.renewals} onActioned={load} />}
      {tab === 'documents' && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="client-documents">
          <div className="flex items-center gap-2 mb-2">
            <FolderOpen className="w-4 h-4" style={{ color: C.blue }} />
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Client Documents</h3>
          </div>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>
            Drag &amp; drop files anywhere below (or click Upload) — each document is read with AI and filed against this client.
          </p>
          <RecordDocuments recordType="client" recordId={e.entity_id} enableDrop />
        </Card>
      )}
    </div>
  );
}

function TimelineCard({ items }) {
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="client-timeline">
      {items.length === 0 ? <p className="text-sm py-4 text-center" style={{ color: C.slate }}>No history for this client yet.</p> : (
        <div className="space-y-0">
          {items.map((t, i) => {
            const ts = TYPE_STYLE[t.type] || TYPE_STYLE.policy;
            const Icon = ts.icon;
            return (
              <div key={i} className="flex items-center gap-3 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`timeline-row-${i}`}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: ts.color + '12' }}>
                  <Icon className="w-3 h-3" style={{ color: ts.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: C.navy }}>{t.label}</p>
                  <p className="text-[10px]" style={{ color: C.slate }}>{t.date ? new Date(t.date).toLocaleDateString('en-AU') : '—'}</p>
                </div>
                {t.amount != null && <span className="text-xs font-mono shrink-0" style={{ color: C.navy }}>{fmt$(t.amount)}</span>}
                <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0" style={{ background: (STATUS_COLOR[t.status] || C.slate) + '15', color: STATUS_COLOR[t.status] || C.slate }}>{(t.status || '').replace(/_/g, ' ')}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

const ACTIONABLE = ['quote_generated', 'offered'];

function RenewalsCard({ rows, onActioned }) {
  const api = useApi();
  const [acting, setActing] = useState('');

  const act = async (r, action) => {
    if (action === 'decline' && !window.confirm(`Decline the renewal offer for ${r.business_name || r.renewal_reference}?`)) return;
    setActing(r.renewal_id + action);
    try {
      await api.put(`/renewals/${r.renewal_id}/action`, { action, notes: `${action}ed by broker from client file` });
      toast.success(action === 'accept' ? `Renewal ${r.renewal_reference} accepted — new policy bound` : `Renewal ${r.renewal_reference} declined`);
      onActioned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || `Failed to ${action} renewal`);
    }
    setActing('');
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="client-renewals">
      {rows.length === 0 ? <p className="text-sm py-4 text-center" style={{ color: C.slate }}>No renewals for this client.</p> : (
        <div className="space-y-0">
          {rows.map((r, i) => (
            <div key={r.renewal_id || i} className="flex flex-wrap items-center gap-3 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }} data-testid={`renewal-row-${r.renewal_id}`}>
              <span className="font-mono w-40 truncate" style={{ color: C.blue }}>{r.renewal_reference}</span>
              <span className="flex-1 min-w-[120px] truncate" style={{ color: C.navy }}>{r.insurance_class}</span>
              <span className="font-mono" style={{ color: C.navy }}>{fmt$(r.renewal_premium)}</span>
              <span className="hidden md:inline text-[10px]" style={{ color: C.slate }}>{r.current_expiry ? `exp ${new Date(r.current_expiry).toLocaleDateString('en-AU')}` : ''}</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0" style={{ background: (STATUS_COLOR[r.status] || C.slate) + '15', color: STATUS_COLOR[r.status] || C.slate }}>{(r.status || '').replace(/_/g, ' ')}</span>
              {ACTIONABLE.includes(r.status) && (
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => act(r, 'accept')} disabled={!!acting}
                    data-testid={`renewal-accept-${r.renewal_id}`}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: C.green }}>
                    {acting === r.renewal_id + 'accept' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Accept
                  </button>
                  <button onClick={() => act(r, 'decline')} disabled={!!acting}
                    data-testid={`renewal-decline-${r.renewal_id}`}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-red-50 disabled:opacity-50"
                    style={{ border: `1px solid ${C.red}`, color: C.red }}>
                    {acting === r.renewal_id + 'decline' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}Decline
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function BrokerNotes({ clientKey }) {
  const api = useApi();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/broker/clients/${clientKey}/notes`)
      .then(r => setNotes(r.data.notes))
      .catch(() => setNotes([]));
  }, [api, clientKey]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/broker/clients/${clientKey}/notes`, { text: text.trim() });
      setText(''); setOpen(false);
      toast.success('Note pinned to client file');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save note');
    }
    setSaving(false);
  };

  const removeNote = async (noteId) => {
    try {
      await api.delete(`/broker/clients/${clientKey}/notes/${noteId}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove note');
    }
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(232,160,32,0.25)', background: 'rgba(232,160,32,0.03)' }} data-testid="broker-notes-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4" style={{ color: '#B27516' }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
            My Notes{notes ? ` (${notes.length})` : ''}
          </h3>
          <span className="text-[9px]" style={{ color: '#64748B' }}>Only visible to you — carries context between renewals</span>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="broker-note-add-btn"
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-amber-50"
            style={{ border: '1px solid rgba(178,117,22,0.4)', color: '#B27516' }}>
            <Pin className="w-3 h-3" />Pin Note
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2.5">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus
            placeholder="e.g. Client plans a warehouse extension in Q3 — revisit sum insured at renewal…"
            data-testid="broker-note-input" maxLength={2000}
            className="w-full px-3 py-2 text-xs rounded-md border bg-white focus:outline-none focus:ring-1"
            style={{ borderColor: 'rgba(178,117,22,0.3)', color: C.navy }} />
          <div className="flex gap-2 mt-1.5">
            <button onClick={addNote} disabled={saving || !text.trim()} data-testid="broker-note-save-btn"
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: '#B27516' }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />}Pin Note
            </button>
            <button onClick={() => { setOpen(false); setText(''); }} data-testid="broker-note-cancel-btn"
              className="px-3 py-1.5 rounded-md text-[10px] font-medium" style={{ color: C.slate }}>Cancel</button>
          </div>
        </div>
      )}
      {notes && notes.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {notes.map(n => (
            <div key={n.note_id} className="flex items-start gap-2.5 px-3 py-2 rounded-md bg-white" style={{ border: '1px solid rgba(178,117,22,0.15)' }} data-testid={`broker-note-${n.note_id}`}>
              <Pin className="w-3 h-3 mt-0.5 shrink-0" style={{ color: '#B27516' }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs leading-relaxed" style={{ color: C.navy }}>{n.text}</p>
                <p className="text-[9px] mt-0.5" style={{ color: '#64748B' }}>{n.author} · {new Date(n.created_at).toLocaleDateString('en-AU')}</p>
              </div>
              <button onClick={() => removeNote(n.note_id)} title="Remove note" data-testid={`broker-note-delete-${n.note_id}`}
                className="p-1 rounded hover:bg-red-50 transition-colors shrink-0">
                <X className="w-3 h-3" style={{ color: C.red }} />
              </button>
            </div>
          ))}
        </div>
      )}
      {notes && notes.length === 0 && !open && (
        <p className="text-[11px] mt-2" style={{ color: '#64748B' }}>No notes yet — pin anything you'll want to remember at renewal time.</p>
      )}
    </Card>
  );
}

function KPI({ label, value, color }) {
  return (<Card className="px-3 py-2.5 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
    <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
    <p className="text-base font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p>
  </Card>);
}

function ListCard({ rows, empty, render, testid }) {
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
      {rows.length === 0 ? <p className="text-sm py-4 text-center" style={{ color: C.slate }}>{empty}</p> : (
        <div className="space-y-0">
          {rows.map((r, i) => {
            const cols = render(r);
            const status = cols[cols.length - 1];
            return (
              <div key={i} className="flex items-center gap-4 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <span className="font-mono w-40 truncate" style={{ color: C.blue }}>{cols[0]}</span>
                <span className="flex-1 truncate" style={{ color: C.navy }}>{cols[1]}</span>
                <span className="font-mono" style={{ color: C.navy }}>{cols[2]}</span>
                <span className="hidden md:inline text-[10px]" style={{ color: C.slate }}>{cols[3]}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0" style={{ background: (STATUS_COLOR[status] || C.slate) + '15', color: STATUS_COLOR[status] || C.slate }}>{(status || '').replace(/_/g, ' ')}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
